import _ from 'underscore'

const interpolate = (occurrences, stages, chartStartTime, chartEndTime) => {
  if (!(occurrences && occurrences.length)) { return [] }

  // Only stage 1 is relevant since stage 2 is always a subset of stage 1.
  const coolingStage = _.find(stages, stage => { return stage?.stage?.includes('COMPRESSOR_COOLING') })
  if (!coolingStage || !coolingStage.runOccurrences.length) { return [] }

  // A cooling phase is a series of constinuous cooling stages.
  const interpolatedCoolingPhases = interpolateCoolingPhases(coolingStage, chartStartTime, chartEndTime)

  const superheatOccurrencesWithinCoolingPhases =
        findSuperheatOccurrencesWithinCoolingPhases(occurrences, interpolatedCoolingPhases)

  const interpolatedSuperheatOccurrences =
        interpolateAgainstCoolingPhases(
          interpolatedCoolingPhases, superheatOccurrencesWithinCoolingPhases)

  return interpolatedSuperheatOccurrences
}

const interpolateCoolingPhases = (coolingStage, chartStartTime, chartEndTime) => {
  const runOccurrences = _.clone(coolingStage.runOccurrences)

  // If first occurrence started before chart start time, interpolate started occurrence at beginning of timeframe.
  if (runOccurrences && runOccurrences[0].operation === 'stopped') {
    runOccurrences.unshift({ operation: 'started', occurredAt: chartStartTime })
  }

  // If first occurrence stopped after chart end time, interpolate stopped occurrence at end of timeframe.
  if (runOccurrences && runOccurrences[runOccurrences.length - 1].operation === 'started') {
    runOccurrences.push({ operation: 'stopped', occurredAt: chartEndTime })
  }

  const mergedRunOccurrences = mergeConsecutiveRunOccurrences(runOccurrences)

  const started = findOccurrencesByRunningCapacity(mergedRunOccurrences)
  const stopped = findOccurrencesByZeroCapacity(mergedRunOccurrences)

  return _.map(_.zip(started, stopped), pair => {
    return {
      startedAt: pair[0],
      stoppedAt: pair[1]
    }
  })
}

const findOccurrencesByRunningCapacity = (occurrences) => {
  return _.chain(occurrences)
    .filter(occurrence => occurrence.capacity > 0)
    .map(occurrence => occurrence.occurredAt)
    .value()
}

const findOccurrencesByZeroCapacity = (occurrences) => {
  return _.chain(occurrences)
    .filter(occurrence => occurrence.capacity === 0)
    .map(occurrence => occurrence.occurredAt)
    .value()
}

const mergeConsecutiveRunOccurrences = (runOccurrences) => {
  const startedOccurrences = _.filter(runOccurrences, occurrence => occurrence.operation === 'started')

  _.each(startedOccurrences, started => {
    const stopped = _.find(runOccurrences, occurrence => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      occurrence.operation === 'stopped' && occurrence.occurredAt === started.occurredAt
    })

    if (stopped) {
      runOccurrences = _.without(runOccurrences, started, stopped)
    }
  })

  return runOccurrences
}

const findSuperheatOccurrencesWithinCoolingPhases = (occurrences, interpolateCoolingPhases) => {
  return _.filter(occurrences, occurrence => {
    return _.any(interpolateCoolingPhases, phase => {
      return doesOccurrenceExistWithinCoolingPhase(occurrence, phase)
    })
  })
}

const interpolateAgainstCoolingPhases = (coolingPhases, superheatOccurrences) => {
  const additionalInterpolatedOccurrences = []

  _.each(coolingPhases, phase => {
    // Add a point at the start of a cooling phase.
    additionalInterpolatedOccurrences.push({ temperature: 0, occurredAt: phase.startedAt })

    // Adds a point at the end of a cooling phase.
    const lastSuperheatTempWithinStage =
          _.chain(superheatOccurrences)
            .filter(occurrence => occurrence.occurredAt <= phase.stoppedAt && occurrence.occurredAt >= phase.startedAt)
            .last()
            .value()

    if (lastSuperheatTempWithinStage && lastSuperheatTempWithinStage.temperature) {
      additionalInterpolatedOccurrences.push({
        temperature: lastSuperheatTempWithinStage.temperature,
        occurredAt: phase.stoppedAt
      })
    }

    // Stops the chart line at end of the cooling phase.
    additionalInterpolatedOccurrences.push({ temperature: null, occurredAt: phase.stoppedAt + 1 })
  })

  return _.sortBy(superheatOccurrences.concat(additionalInterpolatedOccurrences), occurrence => occurrence.occurredAt)
}

const doesOccurrenceExistWithinCoolingPhase = (occurrence, phase) => {
  return phase.startedAt < occurrence.occurredAt && occurrence.occurredAt < phase.stoppedAt
}

const SuperheatInterpolator = {
  interpolate
}

export default SuperheatInterpolator
